import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import type { ChatStream, ChatStreamListener, SendMessageParams } from '../../fetchers/chat/chat-fetchers';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { chatKeys } from './query-keys';

export type UseSendChatMessage = {
    readonly send: (params: SendMessageParams, listeners?: Partial<ChatStreamListener>) => ChatStream;
    readonly stop: () => void;
};

export const useSendChatMessage = (): UseSendChatMessage => {
    const { sendChatMessage } = useApplicationFetchers();
    const queryClient = useQueryClient();
    const streamRef = useRef<ChatStream | null>(null);

    const stop = useCallback(() => {
        const current = streamRef.current;
        if (current !== null) {
            current.stop();
            streamRef.current = null;
        }
    }, []);

    const send = useCallback(
        (params: SendMessageParams, listeners?: Partial<ChatStreamListener>) => {
            stop();

            const invalidate = (): void => {
                queryClient.invalidateQueries({ queryKey: chatKeys.thread(params.threadId) });
                queryClient.invalidateQueries({ queryKey: chatKeys.threads() });
            };

            const wrapped: Partial<ChatStreamListener> = {
                ...listeners,
                complete: (event) => {
                    listeners?.complete?.(event);
                    invalidate();
                },
                error: (event) => {
                    listeners?.error?.(event);
                    invalidate();
                },
            };

            const stream = sendChatMessage(params, wrapped);
            streamRef.current = stream;
            stream.start();

            return stream;
        },
        [sendChatMessage, queryClient, stop],
    );

    useEffect(() => {
        return () => {
            stop();
        };
    }, [stop]);

    return { send, stop };
};
